<script setup>
import { computed } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { useTaskGroups } from '@/composables/tasks'
import { signalToneEnum, signalToneColor } from '@/composables/dashboard-kpi'

import TaskItem from '@/components/task/TaskItem.vue'
import TaskProgress from '@/components/task/TaskProgress.vue'
import TaskTypeChip from '@/components/task/TaskTypeChip.vue'

const props = defineProps({
  tasks: {
    type: Array,
    required: true,
  },
  splitByType: {
    type: Boolean,
    default: false,
  },
  showCompleted: {
    type: Boolean,
    default: true,
  },
  collapsed: {
    type: Array,
    default: () => [],
  },
  highlightedId: {
    type: Number,
    default: null,
  },
})

const emit = defineEmits(['toggle', 'open', 'update:collapsed'])

const i18n = getI18n()
const { groupByProject } = useTaskGroups()

const groups = computed(() => groupByProject(props.tasks, props.splitByType)
  .map(group => ({
    ...group,
    sections: group.sections
      .map(section => ({ ...section, tasks: props.showCompleted ? section.tasks : section.tasks.filter(task => !task.completed) }))
      .filter(section => section.tasks.length),
    completedTasks: props.showCompleted ? [] : group.sections.flatMap(section => section.tasks.filter(task => task.completed)),
  })))

const warningLabel = ({ overdue, missingServices }) => [
  overdue && i18n.t('common.tasks.overdueCount', { count: overdue }),
  missingServices && i18n.t('common.tasks.missingServicesCount', { count: missingServices }, missingServices),
].filter(Boolean).join(' · ')

const completedKey = (group) => `${group.key}-completed`
const isExpanded = (key, expandedByDefault = true) => props.collapsed.includes(key) !== expandedByDefault
const setExpanded = (key, expanded, expandedByDefault = true) => {
  const collapsed = props.collapsed.filter(item => item !== key)
  emit('update:collapsed', expanded === expandedByDefault ? collapsed : [...collapsed, key])
}
</script>

<template>
  <q-list class="task-groups">
    <q-expansion-item
      v-for="group in groups"
      :key="group.key"
      :model-value="isExpanded(group.key)"
      dense
      header-class="q-px-sm task-groups__header"
      @update:model-value="setExpanded(group.key, $event)"
    >
      <template #header>
        <q-item-section>
          <div class="text-weight-bold ellipsis project-color-border" :style="{ borderLeftColor: group.color ?? 'transparent' }">
            {{ group.label }}
          </div>
        </q-item-section>
        <q-item-section side>
          <div class="row no-wrap items-center q-gutter-x-xs">
            <q-icon
              v-if="warningLabel(group.warnings)"
              name="mdi-alert-outline"
              size="sm"
              :color="signalToneColor(signalToneEnum.ATTENTION)"
            >
              <q-tooltip>{{ warningLabel(group.warnings) }}</q-tooltip>
            </q-icon>
            <task-progress circular :progress="group.progress" />
          </div>
        </q-item-section>
      </template>
      <q-list dense class="task-groups__content">
        <template v-for="section in group.sections" :key="section.key">
          <div v-if="section.taskType !== undefined" class="q-px-sm q-pt-xs">
            <task-type-chip :task-type="section.taskType" />
          </div>
          <task-item
            v-for="task in section.tasks"
            :key="task.id"
            :task="task"
            :show-type="!splitByType"
            :highlighted="task.id === highlightedId"
            @toggle="emit('toggle', $event)"
            @open="emit('open', $event)"
          />
        </template>
        <q-expansion-item
          v-if="group.completedTasks.length"
          :model-value="isExpanded(completedKey(group), false)"
          dense
          dense-toggle
          icon="mdi-check-all"
          header-class="q-px-sm text-grey-7"
          :label="`${i18n.t('common.completed')} (${group.completedTasks.length})`"
          @update:model-value="setExpanded(completedKey(group), $event, false)"
        >
          <task-item
            v-for="task in group.completedTasks"
            :key="task.id"
            :task="task"
            show-type
            :highlighted="task.id === highlightedId"
            @toggle="emit('toggle', $event)"
            @open="emit('open', $event)"
          />
        </q-expansion-item>
      </q-list>
    </q-expansion-item>
  </q-list>
</template>

<style scoped>
.task-groups :deep(.task-groups__header) {
  min-height: 2.75rem;
}

.task-groups__content {
  margin-left: .5rem;
}
</style>
